import styled from '@emotion/styled';
import { Empty as EmptyAntd } from 'antd';
import { useMemo } from 'react';
import emojiCompact from './emoji-compact.json';

export interface Sticker<R> {
  imageUrl: string;
  id: string | number;
  pack?: string;
  name?: string;
  raw: R;
}
type StringSize = 'small' | 'mid' | 'large';
type Size = StringSize | number;
export interface StickerGridsProps<R> {
  size?: Size;
  className?: string;
  stickers: Array<Sticker<R>>;
  /**
   * [n, m] 或 nxm ： 每行n个，最大m行
   */
  layout?: [number, number] | `${number}x${number}`;
  gap?: number;
  showEmoji?: boolean;
  onEmojiChange?: (emoji: any) => void;
  onChange?: (sticker: Sticker<R>) => unknown;
}
const defaultLayout = [4, 3];
export const layoutParser = (layout: StickerGridsProps<any>['layout']) => {
  if (!layout) {
    return defaultLayout;
  }
  if (Array.isArray(layout)) {
    return layout;
  }
  return layout.split('x').map((it) => parseInt(it, 10));
};
const stringSizeMap: Record<StringSize, number> = {
  small: 30,
  mid: 60,
  large: 90,
};
export const sizeParser = (size: Size): number => {
  let num = 30;
  if (typeof size === 'string') {
    num = stringSizeMap[size];
  } else {
    num = size;
  }
  return num;
};
const nameHeight = 16;
const Wrapper = styled.div<{ size: Size; lineWrap: number; maxLine: number; gap: number }>`
  font-size: ${({ size }) => {
    return `${sizeParser(size)}px`;
  }};
  padding-left: 4px;
  width: ${({ lineWrap, gap }) => `calc(${lineWrap}em + ${(lineWrap - 1) * gap}px + 12px)`};
  height: ${({ maxLine, gap }) =>
    `calc(${maxLine}em + ${maxLine * nameHeight + (maxLine - 1) * gap}px)`};
  overflow-y: auto;
  overscroll-behavior: contain;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: hsla(0, 0%, 70.6%, 0);
    opacity: 0;
  }
  &:hover::-webkit-scrollbar-thumb {
    background-color: hsla(0, 0%, 70.6%, 0.5);
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const Flex = styled.div<{ gap: number }>`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: ${({ gap }) => (!gap ? '' : `${gap}px`)};
`;
const StickerItem = styled.div`
  width: 1em;
  /* height: 1em; */
  text-align: center;
  line-height: 0;
  cursor: pointer;
  flex: 0 0 1em;
  align-self: flex-start;
  > img {
    width: 1em;
    height: 1em;
    mix-blend-mode: multiply;
  }
  > p {
    line-height: ${nameHeight}px;
    height: ${nameHeight}px;
    padding: 0;
    margin: 0;
    font-size: 12px;
  }
  margin-bottom: 0;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;
const Empty = styled(EmptyAntd)`
  margin: 8% auto;
`;

const StickerGrids = <R extends any>(props: StickerGridsProps<R>) => {
  const {
    onChange,
    onEmojiChange,
    size = 'mid',
    className,
    stickers,
    layout,
    gap = 2,
    showEmoji,
  } = props;
  const [lineWrap, maxLine] = useMemo(() => layoutParser(layout), [layout]);
  const grids = useMemo(() => {
    if (showEmoji) {
      return emojiCompact.map((emoji) => (
        <div
          className="text-xl p-1 hover:bg-fill-300 cursor-pointer"
          onClick={() => {
            onEmojiChange?.(emoji);
          }}
        >
          {emoji}
        </div>
      ));
    }

    return stickers.length ? (
      stickers.map((it) => {
        return (
          <StickerItem onClick={() => onChange && onChange(it)} key={it.id}>
            <img src={it.imageUrl} alt={`sticker_${it.id}`} />
            <p>{it.name ?? ''}</p>
          </StickerItem>
        );
      })
    ) : (
      <Empty />
    );
  }, [stickers, onChange, showEmoji, onEmojiChange]);
  return (
    <Wrapper size={size} maxLine={maxLine} lineWrap={lineWrap} className={className} gap={gap}>
      <Flex gap={gap}>{grids}</Flex>
    </Wrapper>
  );
};
export default StickerGrids;
